import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Home as AddressIcon,
} from '@mui/icons-material';
import FormField from '../../Common/FormField';

interface Tab1Props {
  sessionId: string;
}

// Tab name must match backend TAB_NAME_MAPPING["tab1_registration"]
const TAB_NAME = 'Registration';

export default function Tab1_Registration({ sessionId }: Tab1Props) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Patient Identification Section */}
      <Paper elevation={1} sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <PersonIcon sx={{ fontSize: 20, mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
            Patient Identification
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          {/* Unique ID */}
          <Grid item xs={12} sm={6} md={4}>
            <FormField
              tabName={TAB_NAME}
              fieldName="unique_id"
              label="Unique ID"
              placeholder="Patient unique identifier"
              size="small"
              fullWidth
            />
          </Grid>

          {/* Date of Registration */}
          <Grid item xs={12} sm={6} md={4}>
            <FormField
              tabName={TAB_NAME}
              fieldName="date_of_registration"
              label="Date of Registration"
              type="date"
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Grid>

          {/* National ID Number */}
          <Grid item xs={12} sm={6} md={4}>
            <FormField
              tabName={TAB_NAME}
              fieldName="national_id_number"
              label="National ID Number"
              placeholder="National ID / Passport"
              size="small"
              fullWidth
            />
          </Grid>

          {/* Patient Name */}
          <Grid item xs={12} sm={6}>
            <FormField
              tabName={TAB_NAME}
              fieldName="patient_name"
              label="Patient Name *"
              placeholder="Enter patient's full name"
              size="small"
              fullWidth
              required
            />
          </Grid>

          {/* Date of Birth */}
          <Grid item xs={12} sm={6} md={3}>
            <FormField
              tabName={TAB_NAME}
              fieldName="date_of_birth"
              label="Date of Birth"
              type="date"
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Age */}
          <Grid item xs={12} sm={6} md={3}>
            <FormField
              tabName={TAB_NAME}
              fieldName="age"
              label="Age"
              placeholder="Years"
              type="number"
              size="small"
              fullWidth
            />
          </Grid>

          {/* Gender */}
          <Grid item xs={12} sm={6} md={3}>
            <FormField
              tabName={TAB_NAME}
              fieldName="gender"
              label="Gender"
              placeholder="Male / Female / Other"
              size="small"
              fullWidth
            />
          </Grid>

          {/* Email ID */}
          <Grid item xs={12} sm={6} md={3}>
            <FormField
              tabName={TAB_NAME}
              fieldName="email_id"
              label="Email"
              placeholder="patient@example.com"
              type="email"
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Contact Information Section */}
      <Paper elevation={1} sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <PhoneIcon sx={{ fontSize: 20, mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
            Contact Information
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          {/* Primary Contact with Country Code */}
          <Grid item xs={12} sm={6}>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <FormField
                  tabName={TAB_NAME}
                  fieldName="prefix"
                  label="Code"
                  placeholder="+91"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={8}>
                <FormField
                  tabName={TAB_NAME}
                  fieldName="patient_contact_number"
                  label="Contact Number *"
                  placeholder="Primary phone"
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Alternate Contact with Country Code */}
          <Grid item xs={12} sm={6}>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <FormField
                  tabName={TAB_NAME}
                  fieldName="prefix_alternate"
                  label="Code"
                  placeholder="+91"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={8}>
                <FormField
                  tabName={TAB_NAME}
                  fieldName="alternate_contact_number"
                  label="Alternate Contact"
                  placeholder="Secondary phone"
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Address Section */}
      <Paper elevation={1} sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <AddressIcon sx={{ fontSize: 20, mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
            Address
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          {/* Full Address */}
          <Grid item xs={12}>
            <FormField
              tabName={TAB_NAME}
              fieldName="patient_address"
              label="Residential Address"
              placeholder="Enter complete address"
              multiline
              rows={2}
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
